import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Bot,
  Brain,
  User as UserIcon,
  ChevronDown,
  ChevronRight,
  Plus,
  Copy,
  Save,
  RotateCcw,
  FilePlus,
  FileEdit,
  CheckCircle2,
  Wrench,
  AlertCircle,
  Eye,
  EyeOff,
  Lightbulb,
  Target,
  ListChecks,
  Code,
  Sparkles,
} from 'lucide-react';
import { CodeChangePills } from './CodeChangePills';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import { AiParticipantSpeaker } from './AiVoiceControls';
import { formatTimeOnly } from '../helpers/formatDateUTC';
import { UserAvatar } from './UserAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { RichMessageContent } from '../helpers/RichMessageContent';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { parseStreamingPlan } from '../helpers/aiPlanningParser';
import { parsePlanBlock } from '../helpers/planBlockParser';
import { PlanCard } from './PlanCard';
import { AiPhasePills, PhaseId, PHASE_ICONS, PhaseStatus } from './AiPhasePills';
import { CodeSuggestion } from './AiCodeSuggestions';
import { ChatMessage } from '../helpers/chatApiClient';
import { StreamingMessage } from '../helpers/useAiChat';
import { extractFilesFromMarkdown } from '../helpers/extractFilesFromMarkdown';
import { useCreateSecret } from '../helpers/useSecretsApi';
import { type Recommendation } from './RecommendationsModal';
import { TipsAndRecommendations } from './TipsAndRecommendations';
import { fetchAiRecommendations } from '../endpoints/ai/recommendations_POST.schema';
import { MessageFeedback } from './MessageFeedback';
import { GlbViewer, isGlbFile } from './GlbViewer';
import styles from './AiChatMessage.module.css';

// Collapsible code block accordion — header always visible, body toggles
const CollapsibleCodeBlock: React.FC<{
  filename: string;
  code: string;
  language?: string;
  defaultCollapsed?: boolean;
}> = ({ filename, code, language, defaultCollapsed = true }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const lineCount = code.split('\n').length;

  return (
    <div className={styles.codeBlockItem}>
      <div
        className={styles.codeBlockHeader}
        onClick={() => setCollapsed((c) => !c)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.codeBlockHeaderLeft}>
          <div className={styles.codeBlockTitleRow}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            <span
              className={`${styles.codeBlockFilename} ${styles.clickableFile}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('tasking.openFile', { detail: { filename } }));
              }}
              title={`Open ${filename}`}
            >
              {filename}
            </span>
          </div>
          <span className={styles.codeBlockMeta}>{lineCount} lines</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(code);
            toast.success('Copied to clipboard');
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
      {!collapsed && (
        <pre className={styles.codeBlock}>
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
};

// Simple string hash for consistent numeric seed from personaId
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Generate unique robot avatar URL using RoboHash
const getRobotAvatar = (personaId: string | number): string => {
  const seed = typeof personaId === 'string' ? hashString(personaId) : personaId;
  return `https://robohash.org/${seed}.png?set=set1&size=64x64`;
};

interface AiChatMessageProps {
  message: ChatMessage | StreamingMessage;
  sessionId?: string;
  isStreaming?: boolean;
  onCreateCheckpoint?: () => void;
  onRestoreCheckpoint?: (checkpointId: string) => void;
  onSuggestionSelect?: (suggestionCode: string) => void;
  onViewFileDiff?: (checkpointId: string, filename: string) => void; // View diff for a specific file
  onFixStep?: (stepText: string) => void; // Callback when user clicks fix button on an error step
  userStatus?: 'online' | 'meeting' | 'away' | 'offline' | null;
  userAvatarUrlFallback?: string | null; // Fallback avatar URL from presence/auth
  currentFile?: string; // Current file path for context
  currentUserId?: number;
  currentUserDisplayName?: string | null;
  hideStepsAndCode?: boolean; // Hide [STEPS] and [CODE] sections (used when tabs show these separately)
  phaseFilter?: 'steps' | 'code'; // Show only this phase (for split message display)
  showOnlyStepsAndCode?: boolean; // Show only Steps and Code phases (with suggestions)
  autoApplyRecommendations?: boolean; // Auto-apply high-priority recommendations in autonomous mode
  participantAvatarMap?: Record<string, string>; // personaId → resolved avatar URL (matches participants bar)
  onRegenerate?: (prompt: string) => void;
}

interface MessageWithPersona extends ChatMessage {
  personaId?: string | null;
  personaName?: string | null;
  personaIcon?: string | null;
  personaCategory?: string | null;
  personaDescription?: string | null;
}

interface StructuredMessage {
  thinking?: string;
  reasoning?: string;
  planning?: string;
  steps?: string[];
  code?: string;
}

interface SecretCandidate {
  keyName: string;
  value: string;
}

const AI_AVATAR_URL = '/icons/3a06bf82-055b-4a63-a832-520377252e43.png';

const THINKING_WORDS = [
  'pondering',
  'reflecting',
  'analyzing',
  'processing',
  'considering',
  'contemplating',
  'deliberating',
  'ruminating',
  'evaluating',
  'assessing',
  'reasoning',
  'inferring',
  'deducing',
  'computing',
  'calculating',
  'modeling',
  'simulating',
  'estimating',
  'theorizing',
  'hypothesizing',
  'brainstorming',
  'ideating',
  'visualizing',
  'conceptualizing',
  'synthesizing',
  'integrating',
  'correlating',
  'comparing',
  'contrasting',
  'mapping',
  'sorting',
  'filtering',
  'scanning',
  'reviewing',
  'inspecting',
  'examining',
  'studying',
  'probing',
  'questioning',
  'rethinking',
  'reframing',
  'decoding',
  'parsing',
  'classifying',
  'categorizing',
  'organizing',
  'structuring',
  'sequencing',
  'prioritizing',
  'planning',
  'projecting',
  'forecasting',
  'anticipating',
  'predicting',
  'iterating',
  'validating',
  'verifying',
  'crosschecking',
  'debugging',
  'tracing',
  'optimizing',
  'refining',
  'polishing',
  'revising',
  'updating',
  'sifting',
  'screening',
  'sensing',
  'discerning',
  'detecting',
  'recognizing',
  'grasping',
  'comprehending',
  'understanding',
  'memorizing',
  'recalling',
  'retrieving',
  'loading',
  'unpacking',
  'unraveling',
  'deconstructing',
  'reconstructing',
  'weighing',
  'balancing',
  'ranking',
  'scoring',
];

function sanitizeSecretValue(raw: string): string {
  return raw
    .trim()
    .replace(/[,;]$/, '')
    .replace(/^["'`]/, '')
    .replace(/["'`]$/, '')
    .trim();
}

function looksLikeSecretKey(keyName: string): boolean {
  const upper = keyName.toUpperCase();
  const signals = [
    'API_KEY',
    'TOKEN',
    'SECRET',
    'PASSWORD',
    'PASS',
    'AUTH',
    'PRIVATE',
    'CLIENT_ID',
    'CLIENT_SECRET',
    'ACCESS_KEY',
    'WEBHOOK',
    'DATABASE_URL',
    'DB_URL',
    'CONNECTION_STRING',
  ];
  return signals.some((signal) => upper.includes(signal));
}

function extractSecretCandidates(content: string): SecretCandidate[] {
  if (!content) return [];
  const candidates = new Map<string, SecretCandidate>();
  const patterns = [
    /(?:^|\n)\s*([A-Z][A-Z0-9_]{2,})\s*=\s*([^\n#]+)/g,
    /(?:^|\n)\s*([A-Z][A-Z0-9_]{2,})\s*:\s*([^\n#]+)/g,
  ];

  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content)) !== null) {
      const keyName = match[1].trim().toUpperCase();
      const value = sanitizeSecretValue(match[2] || '');
      if (!looksLikeSecretKey(keyName)) continue;
      if (!value || value.length < 4) continue;
      if (value.includes(' ')) continue;
      if (!candidates.has(keyName)) {
        candidates.set(keyName, { keyName, value });
      }
      if (candidates.size >= 6) break;
    }
  }

  return Array.from(candidates.values());
}

function maskSecretValue(value: string): string {
  if (value.length <= 6) return '*'.repeat(Math.max(4, value.length));
  return `${value.slice(0, 3)}${'*'.repeat(Math.max(4, value.length - 5))}${value.slice(-2)}`;
}

export const AiChatMessage: React.FC<AiChatMessageProps> = ({
  message,
  sessionId,
  isStreaming = false,
  onCreateCheckpoint,
  onRestoreCheckpoint,
  onSuggestionSelect,
  onViewFileDiff,
  onFixStep,
  userStatus,
  userAvatarUrlFallback,
  currentFile,
  currentUserId,
  currentUserDisplayName,
  hideStepsAndCode = false,
  phaseFilter,
  showOnlyStepsAndCode = false,
  autoApplyRecommendations = false,
  participantAvatarMap,
  onRegenerate,
}) => {
  const isDev = import.meta.env.DEV;
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'thinking',
    'reasoning',
    'planning',
    'steps',
    'code',
  ]);
  const [selectedPhase, setSelectedPhase] = useState<PhaseId | null>(null);
  const [stepsCodePhase, setStepsCodePhase] = useState<'steps' | 'code'>('steps');
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());
  const togglePhaseCollapse = (phase: string) => {
    setCollapsedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phase)) next.delete(phase); else next.add(phase);
      return next;
    });
  };
  const [createdFiles, setCreatedFiles] = useState<string[]>([]);
  const [modifiedFiles, setModifiedFiles] = useState<string[]>([]);
  const [removedFiles, setRemovedFiles] = useState<string[]>([]);
  const [isSecretDialogOpen, setIsSecretDialogOpen] = useState(false);
  const [secretKeyName, setSecretKeyName] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [secretDescription, setSecretDescription] = useState('');
  const [showSecretValue, setShowSecretValue] = useState(false);
  const createSecretMutation = useCreateSecret();
  const [glbModal, setGlbModal] = useState<{ src: string; name?: string } | null>(null);

  // Listen for file creation events to track step completion
  useEffect(() => {
    const handleApplyCheckpoint = (
      e: CustomEvent<{ createdFiles: string[]; updatedFiles: string[]; deletedFiles?: string[] }>,
    ) => {
      const newFiles = e.detail.createdFiles || [];
      const updatedFiles = e.detail.updatedFiles || [];
      const deletedFiles = e.detail.deletedFiles || [];
      setCreatedFiles((prev) => [...new Set([...prev, ...newFiles])]);
      setModifiedFiles((prev) => [...new Set([...prev, ...updatedFiles])]);
      setRemovedFiles((prev) => [...new Set([...prev, ...deletedFiles])]);
    };

    window.addEventListener('tasking.applyCheckpoint', handleApplyCheckpoint as EventListener);
    return () => {
      window.removeEventListener('tasking.applyCheckpoint', handleApplyCheckpoint as EventListener);
    };
  }, []);

  const handleCopy = () => {
    // For structured messages, we want the raw content or a reconstructed version?
    // The requirement says "concatenate all sections" but usually copying the raw markdown/content is better so they get everything.
    // However, if the display is very different, raw might be confusing if it has custom tags.
    // "For structured AI messages, copy the plain text content (concatenate all sections)"
    // The `message.content` contains the raw string with markers like [THINKING], which user might not want if they just want the text.
    // Let's rely on the parsed structure if available to generate a clean version, or just copy raw content if simple.

    const parsed = parseMessage(message.content);
    let textToCopy = message.content;

    if (parsed.isStructured && parsed.structured) {
      const parts = [];
      if (parsed.structured.thinking) parts.push(parsed.structured.thinking);
      if (parsed.structured.reasoning) parts.push(parsed.structured.reasoning);
      if (parsed.structured.planning) parts.push(parsed.structured.planning);
      if (parsed.structured.steps?.length)
        parts.push(parsed.structured.steps.map((s, i) => `${i + 1}. ${s}`).join('\n'));
      if (parsed.structured.code) parts.push(parsed.structured.code);

      if (parts.length > 0) {
        textToCopy = parts.join('\n\n');
      }
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => toast.success('Copied to clipboard!'))
      .catch(() => toast.error('Failed to copy'));
  };

  const parseMessage = (
    content: string,
  ): {
    isStructured: boolean;
    structured?: StructuredMessage;
    plain?: string;
    activePhase?: any;
    plan?: any;
  } => {
    // Strip internal extension/tool markers that should never be displayed to users
    content = content
      // Strip runs of &nbsp; used as vertical spacers by the LLM
      .replace(/(&nbsp;\s*){2,}/gi, ' ')
      .replace(/(\u00a0\s*){2,}/g, ' ')
      .replace(/^[ \t\u00a0]*(&nbsp;[ \t\u00a0]*)+$/gim, '')
      // Collapse 3+ consecutive blank lines
      .replace(/(\n\s*){3,}/g, '\n\n')
      // Complete [ACTIONS]...[/ACTIONS] blocks
      .replace(/\[ACTIONS\][\s\S]*?\[\/ACTIONS\]/g, '')
      // Partial [ACTIONS] block (streaming — closing tag hasn't arrived yet)
      .replace(/\[ACTIONS\][\s\S]*$/g, '')
      // Raw JSON action blocks: {"actions":[...]}
      .replace(/\{"actions"\s*:\s*\[[\s\S]*?\]\s*\}/g, '')
      // Partial raw JSON action block (streaming)
      .replace(/\{"actions"\s*:\s*\[[\s\S]*$/g, '')
      .replace(/\[ACTION_REPORT\][\s\S]*?\[\/ACTION_REPORT\]/g, '')
      .replace(/\[ACTION_REPORT\][\s\S]*$/g, '')
      .replace(/\[EXTENSION_ACTIONS:[^\]]*\]/g, '')
      .replace(/\[TOOL_STATUS:[^\]]*\]/g, '')
      .replace(/\[EXTENSION_STATUS\][^\n]*/g, '')
      .replace(/\[ACTION_RESULTS\][\s\S]*?\[\/ACTION_RESULTS\]/g, '')
      .replace(/\[ACTION_RESULTS\][\s\S]*$/g, '')
      .replace(/\[SCREENSHOT_ANALYSIS\][\s\S]*?\[\/SCREENSHOT_ANALYSIS\]/g, '')
      .replace(/\[SCREENSHOT_ANALYSIS\][\s\S]*$/g, '')
      .trim();

    const hasMarkers =
      content.includes('[THINKING]') ||
      content.includes('[REASONING]') ||
      content.includes('[PLANNING]') ||
      content.includes('[STEPS]') ||
      content.includes('[CODE]');

    // Debug logging to see what content we're receiving
    if (isDev && isStreaming && content.length > 0) {
      console.log('[AiChatMessage] Parsing content (first 500 chars):', content.substring(0, 500));
      console.log('[AiChatMessage] Has markers:', hasMarkers);
    }

    // For messages without markers, treat as structured with inferred phases
    // This ensures users see the AI's thought process even if model doesn't use markers
    if (!hasMarkers) {
      // Infer phases from content patterns for ALL messages (streaming and saved)
      if (content.trim()) {
        // Check if content has code blocks - switch to "code" phase
        const hasCodeBlock = content.includes('```');
        // Check for step-like patterns (numbered lists, bullet points with action verbs)
        const hasSteps =
          /^\s*(\d+\.|[-*•])\s*(Create|Add|Update|Implement|Build|Write|Set up|Configure)/im.test(
            content,
          );

        // Determine phase based on content patterns
        let inferredPhase: 'thinking' | 'planning' | 'steps' | 'code' = 'thinking';
        let thinkingContent = content;
        let planningContent = '';
        let stepsContent: string[] = [];
        let codeContent = '';

        if (hasCodeBlock) {
          inferredPhase = 'code';
          // Split content into pre-code (thinking/planning) and code
          const codeBlockMatch = content.match(/([\s\S]*?)(```[\s\S]*)/);
          if (codeBlockMatch) {
            thinkingContent = codeBlockMatch[1].trim();
            codeContent = codeBlockMatch[2];
          }
        } else if (hasSteps) {
          inferredPhase = 'steps';
          // Steps only: actionable items (e.g. "Create App.tsx"). Code goes in code/filename, not as a step.
          const lines = content.split('\n');
          const stepPattern = /^\s*(\d+\.|[-*•])\s*(.+)/;
          const nonStepLines: string[] = [];
          for (const line of lines) {
            const match = line.match(stepPattern);
            if (match) {
              const text = match[2].trim();
              const isCodeLike =
                /;\s*$|=>|\b(const|let|var|import|return|function|export)\s+/.test(text) ||
                (/[{}()\[\]]|\.\s*\w+\s*\(/.test(text) && /[=;]/.test(text));
              if (!isCodeLike && text.length > 2) {
                stepsContent.push(text);
              } else {
                nonStepLines.push(line);
              }
            } else {
              nonStepLines.push(line);
            }
          }
          thinkingContent = nonStepLines.join('\n').trim();
        }

        return {
          isStructured: true,
          structured: {
            thinking: thinkingContent,
            reasoning: '',
            planning: planningContent,
            steps: stepsContent,
            code: codeContent,
          },
          plan: {
            thinking: thinkingContent,
            reasoning: '',
            planning: planningContent,
            steps: stepsContent,
            code: codeContent,
          },
          activePhase: inferredPhase,
        };
      }
      // For non-streaming messages without markers, show as plain
      return { isStructured: false, plain: content };
    }

    const { plan, activePhase } = parseStreamingPlan(content);
    if (isDev) {
      console.log('[AiChatMessage] Parsed plan:', {
        thinking: plan.thinking?.substring(0, 50),
        reasoning: plan.reasoning?.substring(0, 50),
        planning: plan.planning?.substring(0, 50),
        stepsCount: plan.steps?.length,
        code: plan.code?.substring(0, 50),
        activePhase,
      });
    }

    return {
      isStructured: true,
      structured: {
        thinking: plan.thinking,
        reasoning: plan.reasoning,
        planning: plan.planning,
        steps: plan.steps,
        code: plan.code,
      },
      plan,
      activePhase,
    };
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section],
    );
  };

  const isSectionExpanded = (section: string) => expandedSections.includes(section);

  const parsed = useMemo(() => parseMessage(message.content), [message.content, isDev]);
  const isUser = message.role === 'user';
  const isCurrentUser = isUser && currentUserId !== undefined && message.userId === currentUserId;
  const isParticipant = isUser && currentUserId !== undefined && message.userId !== currentUserId;

  const messageWithPersona = message as MessageWithPersona;
  const isAiParticipant = !isUser && !!messageWithPersona.personaId;
  const personaName = messageWithPersona.personaName;
  const personaIcon = messageWithPersona.personaIcon;

  // Get consistent color class for participant based on their userId
  const participantColorClasses = [
    styles.participantBubble1,
    styles.participantBubble2,
    styles.participantBubble3,
    styles.participantBubble4,
    styles.participantBubble5,
    styles.participantBubble6,
  ];
  const getParticipantBubbleClass = (userId: number): string => {
    const index = userId % participantColorClasses.length;
    return participantColorClasses[index];
  };

  // When an AI message arrives with empty content, show a random thinking word with brain icon
  const randomThinkingWord = useMemo(() => {
    return THINKING_WORDS[Math.floor(Math.random() * THINKING_WORDS.length)];
  }, []);

  // Auto-select active phase when streaming — always follow the latest phase
  // during streaming so the user sees progress. Only stop following if user
  // manually selects a different phase (tracked via userSelectedPhaseRef).
  const userSelectedPhaseRef = useRef(false);
  useEffect(() => {
    if (isStreaming && parsed.activePhase) {
      if (!userSelectedPhaseRef.current) {
        setSelectedPhase(parsed.activePhase);
      }
    }
    // Reset manual selection when streaming stops
    if (!isStreaming) {
      userSelectedPhaseRef.current = false;
    }
  }, [isStreaming, parsed.activePhase]);

  // Extract filename from original message content for suggestions
  const codeFilename = useMemo(() => {
    if (!parsed.isStructured || !message.content) {
      // Fallback to currentFile if available
      return currentFile || null;
    }

    // Try to extract filename from the original message content
    const codeBlockRegex = /```[\w-]*\s*(?:filename\s*=\s*["']?([^"'\s]+)["']?)?/g;
    const fileLabelRegex = /\*\*File:\*\*\s*`([^`]+)`/g;

    // Find filename in code block markers
    let match = codeBlockRegex.exec(message.content);
    if (match && match[1]) {
      return match[1].trim();
    }

    // Find filename in file labels before code blocks
    const codeIndex = message.content.indexOf('[CODE]');
    if (codeIndex !== -1) {
      const beforeCode = message.content.substring(0, codeIndex);
      const fileMatch = fileLabelRegex.exec(beforeCode);
      if (fileMatch && fileMatch[1]) {
        return fileMatch[1].trim();
      }
    }

    // Check for filename in code content itself
    if (parsed.structured?.code) {
      const firstLine = parsed.structured.code.split('\n')[0];
      const fileLineMatch = firstLine.match(/^\s*\/\/\s*filename:\s*(.+)$/i);
      if (fileLineMatch) {
        return fileLineMatch[1].trim();
      }
    }

    // Fallback to currentFile if available
    return currentFile || null;
  }, [parsed.isStructured, message.content, parsed.structured?.code, currentFile]);

  // Only show suggestions after code has been executed (when checkpoint exists)
  const codeExecuted = useMemo(() => {
    // Check if message has a checkpoint, indicating code was executed
    const checkpointId = 'checkpointId' in message ? message.checkpointId : null;
    return !!checkpointId;
  }, [message]);

  // Extract files from message content to show what was created/modified
  const extractedFiles = useMemo(() => {
    if (!message.content || !codeExecuted) return [];
    try {
      return extractFilesFromMarkdown(message.content);
    } catch (err) {
      console.error('[AiChatMessage] Failed to extract files:', err);
      return [];
    }
  }, [message.content, codeExecuted]);

  const secretCandidates = useMemo(
    () => (isUser || isStreaming ? [] : extractSecretCandidates(message.content || '')),
    [isUser, isStreaming, message.content],
  );

  // Fixes stage — shows what the esbuild repair pipeline actually fixed
  const [formattingDone, setFormattingDone] = useState(false);
  const [repairDetails, setRepairDetails] = useState<{ file: string; fixes: string[] }[]>([]);
  useEffect(() => {
    if (!codeExecuted || isUser || isStreaming) return;
    // Collect repair reports from the esbuild plugin
    const reports: { file: string; fixes: string[] }[] = [];
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { file: string; fixes: string[] };
      if (detail?.fixes?.length) reports.push(detail);
    };
    window.addEventListener('preview-repair-report', handler);
    // Give the build pipeline time to emit all reports, then finalize
    const timer = setTimeout(() => {
      window.removeEventListener('preview-repair-report', handler);
      setRepairDetails(reports);
      setFormattingDone(true);
    }, 1200);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('preview-repair-report', handler);
    };
  }, [codeExecuted, isUser, isStreaming]);

  // AI-generated recommendations and pro-tip (replaces hardcoded pattern matching)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [proTip, setProTip] = useState('');
  const [recsLoading, setRecsLoading] = useState(false);
  const recsFetchedRef = useRef(false);

  const extractedFilesRef = useRef(extractedFiles);
  extractedFilesRef.current = extractedFiles;

  useEffect(() => {
    // Only fetch recommendations when auto-apply is enabled (opt-in via session settings)
    if (!autoApplyRecommendations) return;
    // Wait for formatting stage to complete before fetching recommendations
    if (!formattingDone || isUser || isStreaming || recsFetchedRef.current) return;
    if (!sessionId || !message.content || extractedFilesRef.current.length === 0) return;

    recsFetchedRef.current = true;
    setRecsLoading(true);

    fetchAiRecommendations({
      sessionId,
      messageContent: message.content,
      extractedFiles: extractedFilesRef.current.map((f) => ({
        filename: f.filename,
        code: f.code,
      })),
    })
      .then((result) => {
        if (result) {
          setRecommendations(result.recommendations as Recommendation[]);
          setProTip(result.proTip || '');
        }
      })
      .catch((err) => {
        console.debug('[AiChatMessage] Failed to fetch AI recommendations:', err);
      })
      .finally(() => {
        setRecsLoading(false);
      });
  }, [autoApplyRecommendations, formattingDone, isUser, isStreaming, sessionId, message.content]);

  const handleApplyRecommendations = (selectedIds: string[]) => {
    const selected = recommendations.filter((r) => selectedIds.includes(r.id));
    const recommendationList = selected.map((r) => `- **${r.title}**: ${r.description}`).join('\n');
    const prompt = `Please implement the following improvements:\n\n${recommendationList}`;

    // Dispatch a custom event that AiChatPanel listens for
    window.dispatchEvent(
      new CustomEvent('tasking.recommendationSelected', {
        detail: { message: prompt, autoSend: true },
      }),
    );
  };

  // Auto-apply high-priority recommendations when setting is enabled.
  // Uses a ref to track if already auto-applied to prevent re-triggering.
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (!autoApplyRecommendations || !formattingDone || isUser || isStreaming) return;
    if (recommendations.length === 0 || autoAppliedRef.current) return;

    const highPriorityRecs = recommendations.filter((r) => r.priority === 'high');
    if (highPriorityRecs.length === 0) return;

    autoAppliedRef.current = true;
    const timer = setTimeout(() => {
      const selectedIds = highPriorityRecs.map((r) => r.id);
      handleApplyRecommendations(selectedIds);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recommendations ref checked via autoAppliedRef guard
  }, [autoApplyRecommendations, formattingDone, isUser, isStreaming, recommendations.length]);

  const openSecretDialog = (candidate: SecretCandidate) => {
    setSecretKeyName(candidate.keyName);
    setSecretValue(candidate.value);
    setSecretDescription('Added from AI chat suggestion');
    setShowSecretValue(false);
    setIsSecretDialogOpen(true);
  };

  const handleSaveSecret = async () => {
    if (!sessionId) {
      toast.error('No session found for saving secrets.');
      return;
    }
    if (!secretKeyName.trim() || !secretValue.trim()) {
      toast.error('Key name and value are required.');
      return;
    }

    try {
      await createSecretMutation.mutateAsync({
        sessionId,
        keyName: secretKeyName.trim().toUpperCase(),
        value: secretValue.trim(),
        description: secretDescription.trim() || undefined,
      });
      toast.success(`Saved ${secretKeyName.trim().toUpperCase()} to session secrets`);
      setIsSecretDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save secret');
    }
  };

  // Helper to safely access checkpointId if it exists
  const checkpointId = 'checkpointId' in message ? message.checkpointId : null;

  const renderActions = () => (
    <div className={`${styles.actionsBar} ${isUser ? styles.actionsBarUser : styles.actionsBarAi}`}>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={handleCopy}
        className={styles.actionButton}
        title="Copy message"
      >
        <Copy size={14} />
      </Button>

      {/* Restore Button - only if checkpoint exists */}
      {checkpointId && onRestoreCheckpoint && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onRestoreCheckpoint(checkpointId)}
          className={styles.actionButton}
          title={isUser ? 'Restore to before this message' : 'Restore to after this response'}
        >
          <RotateCcw size={14} />
        </Button>
      )}

      {/* Save Checkpoint Button - only for AI messages if handler provided */}
      {!isUser && onCreateCheckpoint && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onCreateCheckpoint}
          className={styles.actionButton}
          title="Save Checkpoint"
        >
          <Save size={14} />
        </Button>
      )}

      {/* Thumbs up/down feedback for AI messages */}
      {!isUser && !isStreaming && message.id && (
        <MessageFeedback
          messageId={message.id}
          source="session"
          sourceId={sessionId}
          messageContent={message.content}
          compact
          inline
        />
      )}
    </div>
  );

  const renderContent = () => {
    if (parsed.isStructured && parsed.structured && !isUser) {
      // Show pills if we have a plan (even if some phases are empty)
      const hasPills = !!parsed.plan;

      // Determine which phase to show - default to first phase with content, or active phase
      const getFirstPhaseWithContent = (): PhaseId => {
        const s = parsed.structured!; // non-null assertion since we're inside parsed.isStructured block
        if (s.thinking) return 'thinking';
        if (s.reasoning) return 'reasoning';
        if (s.planning) return 'planning';
        // Skip steps and code if hideStepsAndCode is true
        if (!hideStepsAndCode) {
          if (s.steps && s.steps.length > 0) return 'steps';
          if (s.code) return 'code';
        }
        return 'thinking'; // fallback
      };

      // When showOnlyStepsAndCode is set, show Steps → Code tabs with optional suggestions
      if (showOnlyStepsAndCode) {
        const hasSteps = parsed.structured?.steps && parsed.structured.steps.length > 0;
        const hasCode = !!parsed.structured?.code;

        // Extract code suggestions from the code section
        const codeSuggestions: CodeSuggestion[] = [];
        let codeBlockCount = 0;
        if (parsed.structured?.code) {
          // Count all code blocks (not just those with filename)
          const allCodeBlocks = parsed.structured.code.match(/```[\s\S]*?```/g) || [];
          codeBlockCount = allCodeBlocks.length;

          // Also extract named files for suggestions
          const codeBlockRegex = /```(\w+)?\s*filename="([^"]+)"\n([\s\S]*?)```/g;
          let match;
          let suggestionIdx = 0;
          while ((match = codeBlockRegex.exec(parsed.structured.code)) !== null) {
            const filename = match[2];
            const code = match[3].trim();
            const language = match[1] || 'plaintext';
            codeSuggestions.push({
              id: `suggestion-${suggestionIdx++}`,
              title: filename,
              description: `${language} file`,
              code: `\`\`\`${language} filename="${filename}"\n${code}\n\`\`\``,
              filename,
            });
          }
        }

        return (
          <div className={styles.structuredContent}>
            {/* Steps/Code phase tabs */}
            <div className={styles.stepsCodeTabs}>
              {hasSteps && (
                <button
                  className={`${styles.stepsCodeTab} ${stepsCodePhase === 'steps' ? styles.stepsCodeTabActive : ''}`}
                  onClick={() => setStepsCodePhase('steps')}
                  title="Steps"
                >
                  <img src="/steps.png" alt="To-dos" className={styles.tabIcon} />
                  <span className={styles.tabLabel}>To-dos</span>
                  <span className={styles.tabCount}>{parsed.structured?.steps?.length || 0}</span>
                </button>
              )}
              {hasCode && (
                <button
                  className={`${styles.stepsCodeTab} ${stepsCodePhase === 'code' ? styles.stepsCodeTabActive : ''}`}
                  onClick={() => setStepsCodePhase('code')}
                  title="Code"
                >
                  <img src="/code.png" alt="Coding" className={styles.tabIcon} />
                  <span className={styles.tabLabel}>Coding</span>
                  <span className={styles.tabCount}>
                    {codeBlockCount || (createdFiles.length > 0 ? createdFiles.length : '✓')}
                  </span>
                </button>
              )}
            </div>

            <div className={styles.phaseContent}>
              {stepsCodePhase === 'steps' && hasSteps && (
                <div className={styles.phaseSection}>
                  {/* Circular progress indicator */}
                  {(() => {
                    // Check which steps are completed based on created files
                    const stepStatuses = parsed.structured!.steps!.map((step) => {
                      // Extract potential filename from step text (e.g., "Create UserAuth.tsx" -> "UserAuth.tsx")
                      const fileMatch = step.match(
                        /(?:create|add|update|modify|edit|write)\s+[`"']?([^\s`"']+\.[a-z]+)[`"']?/i,
                      );
                      if (fileMatch) {
                        const targetFile = fileMatch[1];
                        return createdFiles.some(
                          (f) => f.includes(targetFile) || targetFile.includes(f),
                        );
                      }
                      return false;
                    });
                    const completedCount = stepStatuses.filter(Boolean).length;
                    const totalSteps = parsed.structured!.steps!.length;
                    const progress =
                      totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
                    const allComplete =
                      completedCount === totalSteps && totalSteps > 0 && !isStreaming;

                    return (
                      <>
                        <div className={styles.stepsProgress}>
                          <span
                            className={styles.stepsProgressText}
                            style={{
                              color: isStreaming
                                ? 'var(--muted-foreground)'
                                : progress === 100
                                  ? '#22c55e'
                                  : progress >= 75
                                    ? '#f97316'
                                    : progress >= 50
                                      ? '#f59e0b'
                                      : '#eab308',
                            }}
                          >
                            {isStreaming && <span className={styles.spinnerInline} />}
                            {completedCount}/{totalSteps}
                          </span>
                          {/* Heat map progress bar */}
                          <div className={styles.heatMapBar}>
                            <div
                              className={styles.heatMapFill}
                              style={{
                                width: `${progress}%`,
                                backgroundColor: isStreaming
                                  ? 'var(--muted-foreground)'
                                  : progress === 100
                                    ? '#22c55e'
                                    : progress >= 75
                                      ? '#f97316'
                                      : progress >= 50
                                        ? '#f59e0b'
                                        : '#eab308',
                              }}
                            />
                          </div>
                        </div>
                        <div className={styles.stepsList}>
                          {parsed.structured!.steps!.map((step, stepIdx) => {
                            const isComplete = stepStatuses[stepIdx];
                            // Check if step mentions an error pattern
                            const hasError =
                              step.toLowerCase().includes('error') ||
                              step.toLowerCase().includes('failed') ||
                              step.toLowerCase().includes('fix');
                            return (
                              <div
                                key={stepIdx}
                                className={`${styles.stepItem} ${isComplete ? styles.stepCompleted : ''} ${hasError && !isComplete ? styles.stepError : ''}`}
                              >
                                <span className={styles.stepNumber}>{stepIdx + 1}.</span>
                                <span className={styles.stepText}>{step}</span>
                                {isComplete ? (
                                  <CheckCircle2 size={14} className={styles.stepDoneIcon} />
                                ) : hasError ? (
                                  <button
                                    className={styles.stepFixButton}
                                    title="Click to fix this error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onFixStep) {
                                        onFixStep(step);
                                      } else {
                                        // Fallback: dispatch custom event for AI to fix
                                        window.dispatchEvent(
                                          new CustomEvent('tasking.fixError', {
                                            detail: { stepText: step },
                                          }),
                                        );
                                        toast.info(
                                          'Asking AI to fix: ' + step.substring(0, 50) + '...',
                                        );
                                      }
                                    }}
                                  >
                                    <AlertCircle size={14} className={styles.stepErrorIcon} />
                                    <Wrench size={12} className={styles.stepWrenchIcon} />
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {stepsCodePhase === 'code' && hasCode && (
                <div className={styles.phaseSection}>
                  {/* Show created/modified/removed files if available */}
                  {(createdFiles.length > 0 ||
                    modifiedFiles.length > 0 ||
                    removedFiles.length > 0) && (
                    <div className={styles.changeSummary}>
                      <div className={styles.changeSummaryTitle}>
                        <CheckCircle2 size={14} className={styles.successIcon} />
                        Files changed
                      </div>
                      <div className={styles.changeSummaryList}>
                        {createdFiles.map((file, idx) => (
                          <div
                            key={`new-${idx}`}
                            className={`${styles.changeSummaryItem} ${styles.created} ${styles.clickableFile}`}
                            onClick={() => {
                              window.dispatchEvent(
                                new CustomEvent('tasking.openFile', { detail: { filename: file } }),
                              );
                            }}
                            title={`Open ${file}`}
                          >
                            <span className={styles.changeIndicator} data-type="added">
                              +
                            </span>
                            <span>{file}</span>
                          </div>
                        ))}
                        {modifiedFiles.map((file, idx) => (
                          <div
                            key={`mod-${idx}`}
                            className={`${styles.changeSummaryItem} ${styles.modified} ${styles.clickableFile}`}
                            onClick={() => {
                              window.dispatchEvent(
                                new CustomEvent('tasking.openFile', { detail: { filename: file } }),
                              );
                            }}
                            title={`Open ${file}`}
                          >
                            <span className={styles.changeIndicator} data-type="modified">
                              M
                            </span>
                            <span>{file}</span>
                          </div>
                        ))}
                        {removedFiles.map((file, idx) => (
                          <div
                            key={`del-${idx}`}
                            className={`${styles.changeSummaryItem} ${styles.deleted}`}
                            title="File was removed"
                          >
                            <span className={styles.changeIndicator} data-type="removed">
                              -
                            </span>
                            <span>{file}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Render code blocks as collapsible accordions */}
                  {parsed.structured?.code &&
                    (() => {
                      const extractedFiles = extractFilesFromMarkdown(parsed.structured.code);
                      if (extractedFiles.length > 0) {
                        return (
                          <div className={styles.codeBlocksContainer}>
                            {extractedFiles.map((file, idx) => (
                              <CollapsibleCodeBlock
                                key={idx}
                                filename={file.filename}
                                code={file.code}
                                language={file.language}
                                defaultCollapsed={true}
                              />
                            ))}
                          </div>
                        );
                      }
                      // Fallback: show raw code if no files extracted
                      return (
                        <CollapsibleCodeBlock
                          filename="code"
                          code={parsed.structured.code}
                          defaultCollapsed={true}
                        />
                      );
                    })()}
                </div>
              )}
            </div>
          </div>
        );
      }

      // When phaseFilter is set, only show that specific phase (legacy)
      if (phaseFilter) {
        return null; // Deprecated, use showOnlyStepsAndCode instead
      }

      // When hiding steps/code, filter out those phases from selection
      const effectiveSelectedPhase =
        hideStepsAndCode && (selectedPhase === 'steps' || selectedPhase === 'code')
          ? null
          : selectedPhase;
      const effectiveActivePhase =
        hideStepsAndCode && (parsed.activePhase === 'steps' || parsed.activePhase === 'code')
          ? null
          : parsed.activePhase;

      // For non-streaming (completed) messages, default to first phase with content (thinking first)
      // For streaming messages, follow the activePhase to track what the AI is currently writing
      const phaseToShow =
        effectiveSelectedPhase ||
        (isStreaming ? effectiveActivePhase : null) ||
        getFirstPhaseWithContent();

      // Compute phase status for inline traffic lights
      const PHASE_ORDER: PhaseId[] = ['thinking', 'reasoning', 'planning', 'steps', 'code'];
      const activeIdx = effectiveActivePhase ? PHASE_ORDER.indexOf(effectiveActivePhase) : -1;
      const getPhaseStatus = (phase: PhaseId): PhaseStatus => {
        if (phase === effectiveActivePhase) return 'running';
        const idx = PHASE_ORDER.indexOf(phase);
        const hasContent = phase === 'steps'
          ? (parsed.plan?.steps?.length || 0) > 0
          : !!(parsed.structured?.[phase as keyof typeof parsed.structured] as string)?.length;
        if (hasContent) {
          if (activeIdx !== -1 && idx < activeIdx) return 'done';
          if (effectiveActivePhase === null) return 'done';
        }
        return 'idle';
      };

      // Inline traffic light (3 dots: red/yellow/green) for each phase section
      const InlineTrafficLight = ({ phase }: { phase: PhaseId }) => {
        const status = getPhaseStatus(phase);
        return (
          <span className={styles.inlineTrafficLights}>
            <span className={`${styles.inlineLight} ${styles.inlineLightRed} ${status === 'idle' || status === 'error' ? styles.inlineLightOn : ''}`} />
            <span className={`${styles.inlineLight} ${styles.inlineLightYellow} ${status === 'running' ? styles.inlineLightOn : ''}`} />
            <span className={`${styles.inlineLight} ${styles.inlineLightGreen} ${status === 'done' ? styles.inlineLightOn : ''}`} />
          </span>
        );
      };

      return (
        <div className={styles.structuredContent}>
          {hasPills && (
            <AiPhasePills
              plan={parsed.plan!}
              activePhase={effectiveActivePhase}
              selectedPhase={phaseToShow}
              onPhaseSelect={(phase) => {
                userSelectedPhaseRef.current = true;
                setSelectedPhase(phase);
              }}
              hideStepsAndCode={hideStepsAndCode}
            />
          )}

          {/* During streaming: sequential conversational flow (all phases visible as they appear)
              After streaming (completed): expandable sections (show all phases sequentially) */}
          {hasPills && isStreaming ? (
            <div className={styles.streamingFlow}>
              {/* Thinking — collapsible accordion */}
              {parsed.structured.thinking && (
                <div
                  className={`${styles.streamingPhase} ${parsed.activePhase === 'thinking' ? styles.streamingPhaseActive : ''}`}
                >
                  <div className={styles.streamingPhaseLabel} onClick={() => togglePhaseCollapse('thinking')} style={{ cursor: 'pointer' }}>
                    <Brain size={12} />
                    <span>Thinking</span>
                    <InlineTrafficLight phase="thinking" />
                    <span className={styles.accordionChevron}>{collapsedPhases.has('thinking') ? '▸' : '▾'}</span>
                  </div>
                  {!collapsedPhases.has('thinking') && (
                    <div className={styles.streamingThinkingText}>{parsed.structured.thinking}</div>
                  )}
                </div>
              )}

              {/* Reasoning — collapsible accordion */}
              {parsed.structured.reasoning && (
                <div
                  className={`${styles.streamingPhase} ${parsed.activePhase === 'reasoning' ? styles.streamingPhaseActive : ''}`}
                >
                  <div className={styles.streamingPhaseLabel} onClick={() => togglePhaseCollapse('reasoning')} style={{ cursor: 'pointer' }}>
                    <Lightbulb size={12} />
                    <span>Reasoning</span>
                    <InlineTrafficLight phase="reasoning" />
                    <span className={styles.accordionChevron}>{collapsedPhases.has('reasoning') ? '▸' : '▾'}</span>
                  </div>
                  {!collapsedPhases.has('reasoning') && (
                    <div className={styles.streamingReasoningText}>{parsed.structured.reasoning}</div>
                  )}
                </div>
              )}

              {/* Planning — collapsible accordion */}
              {parsed.structured.planning && (
                <div
                  className={`${styles.streamingPhase} ${parsed.activePhase === 'planning' ? styles.streamingPhaseActive : ''}`}
                >
                  <div className={styles.streamingPhaseLabel} onClick={() => togglePhaseCollapse('planning')} style={{ cursor: 'pointer' }}>
                    <Target size={12} />
                    <span>Plan</span>
                    <InlineTrafficLight phase="planning" />
                    <span className={styles.accordionChevron}>{collapsedPhases.has('planning') ? '▸' : '▾'}</span>
                  </div>
                  {!collapsedPhases.has('planning') && (
                    <div className={styles.streamingPlanningText}>{parsed.structured.planning}</div>
                  )}
                </div>
              )}

              {/* Steps — collapsible accordion */}
              {parsed.structured.steps &&
                parsed.structured.steps.length > 0 &&
                !hideStepsAndCode && (
                  <div
                    className={`${styles.streamingPhase} ${parsed.activePhase === 'steps' ? styles.streamingPhaseActive : ''}`}
                  >
                    <div className={styles.streamingPhaseLabel} onClick={() => togglePhaseCollapse('steps')} style={{ cursor: 'pointer' }}>
                      <ListChecks size={12} />
                      <span>Steps</span>
                      <InlineTrafficLight phase="steps" />
                      <span className={styles.accordionChevron}>{collapsedPhases.has('steps') ? '▸' : '▾'}</span>
                    </div>
                    {!collapsedPhases.has('steps') && (
                      <div className={styles.stepsList}>
                        {parsed.structured.steps.map((step, stepIdx) => (
                          <div key={stepIdx} className={styles.stepItem}>
                            <span className={styles.stepNumber}>{stepIdx + 1}.</span>
                            <span className={styles.stepText}>{step}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              {/* Code — collapsible accordion */}
              {parsed.structured.code && !hideStepsAndCode && (
                <div
                  className={`${styles.streamingPhase} ${parsed.activePhase === 'code' ? styles.streamingPhaseActive : ''}`}
                >
                  <div className={styles.streamingPhaseLabel} onClick={() => togglePhaseCollapse('code')} style={{ cursor: 'pointer' }}>
                    <Code size={12} />
                    <span>Code</span>
                    <InlineTrafficLight phase="code" />
                    <span className={styles.accordionChevron}>{collapsedPhases.has('code') ? '▸' : '▾'}</span>
                  </div>
                  {!collapsedPhases.has('code') && (
                    <>
                      {codeExecuted && extractedFiles.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <CodeChangePills
                            files={extractedFiles.map((f) => {
                              const isNew = createdFiles.some(
                                (cf) => cf.includes(f.filename) || f.filename.includes(cf),
                              );
                              const isMod = modifiedFiles.some(
                                (mf) => mf.includes(f.filename) || f.filename.includes(mf),
                              );
                              const isDel = removedFiles.some(
                                (rf) => rf.includes(f.filename) || f.filename.includes(rf),
                              );
                              const changeType = isDel
                                ? 'removed'
                                : isNew
                                  ? 'added'
                                  : isMod
                                    ? 'modified'
                                    : 'added';
                              const lineCount = f.code.split('\n').length;
                              return {
                                filename: f.filename,
                                code: f.code,
                                changeType,
                                added: changeType === 'removed' ? 0 : lineCount,
                                removed:
                                  changeType === 'added'
                                    ? 0
                                    : changeType === 'removed'
                                      ? lineCount
                                      : undefined,
                              };
                            })}
                            onOpenFile={(filename) => {
                              window.dispatchEvent(
                                new CustomEvent('tasking.openFile', { detail: { filename } }),
                              );
                            }}
                            onViewDiff={(filename) => {
                              if (checkpointId && onViewFileDiff) {
                                onViewFileDiff(checkpointId, filename);
                              } else if (checkpointId && onRestoreCheckpoint) {
                                onRestoreCheckpoint(checkpointId);
                              }
                            }}
                            onRevert={(filename) => {
                              window.dispatchEvent(
                                new CustomEvent('tasking.revertFile', {
                                  detail: { filename, checkpointId },
                                }),
                              );
                            }}
                            onRedo={(filename) => {
                              const file = extractedFiles.find((ef) => ef.filename === filename);
                              if (file) {
                                window.dispatchEvent(
                                  new CustomEvent('tasking.redoFile', {
                                    detail: { filename, code: file.code },
                                  }),
                                );
                              }
                            }}
                          />
                        </div>
                      )}
                      {(() => {
                        const codeFiles = extractFilesFromMarkdown(parsed.structured.code);
                        if (codeFiles.length > 0) {
                          return (
                            <div className={styles.codeBlocksContainer}>
                              {codeFiles.map((file, idx) => (
                                <CollapsibleCodeBlock
                                  key={idx}
                                  filename={file.filename}
                                  code={file.code}
                                  language={file.language}
                                  defaultCollapsed={true}
                                />
                              ))}
                            </div>
                          );
                        }
                        return (
                          <CollapsibleCodeBlock
                            filename="code"
                            code={parsed.structured.code}
                            defaultCollapsed={true}
                          />
                        );
                      })()}
                    </>
                  )}
                </div>
              )}

              {/* Fixes stage — visible step between Code and Suggestions */}
              {!isStreaming && codeExecuted && (
                <div
                  className={`${styles.streamingPhase} ${!formattingDone ? styles.streamingPhaseActive : ''}`}
                >
                  <div className={styles.streamingPhaseLabel}>
                    <Sparkles size={12} />
                    <span>Applying fixes</span>
                    {!formattingDone && <span className={styles.streamingDot} />}
                    {formattingDone && (
                      <CheckCircle2 size={12} style={{ color: 'var(--success, #10b981)' }} />
                    )}
                    {formattingDone && repairDetails.length > 0 && (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          color: 'var(--muted-foreground)',
                          marginLeft: 4,
                        }}
                      >
                        {repairDetails
                          .flatMap((r) => r.fixes)
                          .filter((v, i, a) => a.indexOf(v) === i)
                          .join(', ')}
                      </span>
                    )}
                    {formattingDone && repairDetails.length === 0 && (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          color: 'var(--muted-foreground)',
                          marginLeft: 4,
                        }}
                      >
                        No issues found
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Expandable sections: used for completed messages (all phases visible) and streaming without pills
            <>
              {/* Thinking Section */}
              {parsed.structured.thinking && (
                <div className={styles.structuredSection}>
                  <div className={styles.sectionHeader} onClick={() => toggleSection('thinking')}>
                    {isSectionExpanded('thinking') ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <span className={styles.sectionTitle}>Thinking</span>
                  </div>
                  {isSectionExpanded('thinking') && (
                    <div className={styles.sectionContent}>
                      <div className={styles.thinkingText}>{parsed.structured.thinking}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Reasoning Section */}
              {parsed.structured.reasoning && (
                <div className={styles.structuredSection}>
                  <div className={styles.sectionHeader} onClick={() => toggleSection('reasoning')}>
                    {isSectionExpanded('reasoning') ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <span className={styles.sectionTitle}>Reasoning</span>
                  </div>
                  {isSectionExpanded('reasoning') && (
                    <div className={styles.sectionContent}>
                      <div className={styles.reasoningText}>{parsed.structured.reasoning}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Planning Section */}
              {parsed.structured.planning && (
                <div className={styles.structuredSection}>
                  <div className={styles.sectionHeader} onClick={() => toggleSection('planning')}>
                    {isSectionExpanded('planning') ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <span className={styles.sectionTitle}>Planning</span>
                  </div>
                  {isSectionExpanded('planning') && (
                    <div className={styles.sectionContent}>
                      <div className={styles.planningText}>{parsed.structured.planning}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Steps Section */}
              {parsed.structured.steps && parsed.structured.steps.length > 0 && (
                <div className={styles.structuredSection}>
                  <div className={styles.sectionHeader} onClick={() => toggleSection('steps')}>
                    {isSectionExpanded('steps') ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <span className={styles.sectionTitle}>To-dos</span>
                  </div>
                  {isSectionExpanded('steps') && (
                    <div className={styles.sectionContent}>
                      <div className={styles.stepsList}>
                        {parsed.structured.steps.map((step, stepIdx) => (
                          <div key={stepIdx} className={styles.stepItem}>
                            <div className={styles.stepContent}>
                              <span className={styles.stepNumber}>{stepIdx + 1}.</span>
                              <span className={styles.stepText}>{step}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Code Section - Always show code (only if not already shown in pills) */}
              {!hasPills && parsed.structured.code && (
                <div className={styles.structuredSection}>
                  <div className={styles.sectionHeader} onClick={() => toggleSection('code')}>
                    {isSectionExpanded('code') ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <span className={styles.sectionTitle}>Coding</span>
                  </div>
                  {isSectionExpanded('code') && (
                    <div className={styles.sectionContent}>
                      <pre className={styles.codeBlock}>
                        <code>{parsed.structured.code}</code>
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Fixes stage — shows what was fixed before suggestions */}
              {!isStreaming && codeExecuted && (
                <div className={styles.structuredSection}>
                  <div className={styles.sectionHeader}>
                    <Sparkles size={14} />
                    <span className={styles.sectionTitle}>Applying fixes</span>
                    {formattingDone && (
                      <CheckCircle2
                        size={14}
                        style={{ color: 'var(--success, #10b981)', marginLeft: 'auto' }}
                      />
                    )}
                    {!formattingDone && (
                      <span className={styles.streamingDot} style={{ marginLeft: 'auto' }} />
                    )}
                  </div>
                  {formattingDone && (
                    <div
                      style={{
                        padding: '6px 12px 8px',
                        fontSize: '0.75rem',
                        color: 'var(--muted-foreground)',
                        lineHeight: 1.5,
                      }}
                    >
                      {repairDetails.length > 0 ? (
                        repairDetails.map((r, i) => (
                          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                            <span
                              style={{ fontWeight: 600, color: 'var(--foreground)', flexShrink: 0 }}
                            >
                              {r.file}:
                            </span>
                            <span>{r.fixes.join(', ')}</span>
                          </div>
                        ))
                      ) : (
                        <span>No issues found</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* AI-generated ProTip + Recommendations - shown after formatting completes */}
              {!isStreaming &&
                formattingDone &&
                (recommendations.length > 0 || proTip) &&
                !autoApplyRecommendations && (
                  <div className={styles.structuredSection}>
                    <TipsAndRecommendations
                      tip={proTip}
                      recommendations={recommendations}
                      onApplyRecommendations={handleApplyRecommendations}
                      isApplying={recsLoading}
                    />
                  </div>
                )}

              {/* Suggestions now shown as separate bubble after message */}
            </>
          )}
        </div>
      );
    }

    // ── Plan block rendering ──
    const contentForPlan = parsed.plain || message.content || '';
    if (contentForPlan.includes('[PLAN_BLOCK]')) {
      const block = parsePlanBlock(contentForPlan);
      if (block) {
        const handleSelectOption = (questionId: string, option: string) => {
          const msg = `${option}`;
          window.dispatchEvent(
            new CustomEvent('tasking.prefillChat', { detail: { message: msg, autoSend: true } }),
          );
        };
        return (
          <div>
            {block.before && <div className={styles.plainText}>{block.before}</div>}
            <PlanCard data={block.data} onSelectOption={handleSelectOption} />
            {block.after && <div className={styles.plainText}>{block.after}</div>}
          </div>
        );
      }
    }

    // Render plain text messages with image/video/audio/PDF support
    if (parsed.plain) {
      return (
        <div className={styles.plainText}>
          <RichMessageContent content={parsed.plain} onRegenerate={onRegenerate} />
        </div>
      );
    }

    // Fallback if content is empty or undefined - show thinking indicator with brain icon
    if (!message.content || !message.content.trim() || message.content === '(Empty message)') {
      return (
        <div className={styles.thinkingIndicator}>
          <Brain size={14} className={styles.thinkingBrainIcon} />
          <span className={styles.thinkingWord}>{randomThinkingWord}...</span>
        </div>
      );
    }
    return (
      <div className={styles.plainText}>
        <RichMessageContent content={message.content} onRegenerate={onRegenerate} />
      </div>
    );
  };

  // Get the raw displayable content for the assistant bubble.
  // Strips internal markers but keeps the visible text/markdown.
  const getPlainContent = (): string => {
    let c = message.content || '';
    // Strip internal markers
    c = c
      .replace(/\[ACTIONS\][\s\S]*?\[\/ACTIONS\]/g, '')
      .replace(/\[ACTIONS\][\s\S]*$/g, '')
      .replace(/\{"actions"\s*:\s*\[[\s\S]*?\]\s*\}/g, '')
      .replace(/\{"actions"\s*:\s*\[[\s\S]*$/g, '')
      .replace(/\[ACTION_REPORT\][\s\S]*?\[\/ACTION_REPORT\]/g, '')
      .replace(/\[ACTION_REPORT\][\s\S]*$/g, '')
      .replace(/\[ACTION_RESULTS\][\s\S]*?\[\/ACTION_RESULTS\]/g, '')
      .replace(/\[ACTION_RESULTS\][\s\S]*$/g, '')
      .replace(/\[EXTENSION_ACTIONS:[^\]]*\]/g, '')
      .replace(/\[TOOL_STATUS:[^\]]*\]/g, '')
      .replace(/\[EXTENSION_STATUS\][^\n]*/g, '')
      .replace(/\[SCREENSHOT_ANALYSIS\][\s\S]*?\[\/SCREENSHOT_ANALYSIS\]/g, '')
      .replace(/\[SCREENSHOT_ANALYSIS\][\s\S]*$/g, '')
      // Strip phase markers but keep their content
      .replace(/\[(THINKING|REASONING|PLANNING|STEPS|CODE)\]/g, '')
      .trim();
    return c;
  };

  return (
    <div
      className={`${styles.messageRow} ${isUser ? styles.userRow : styles.assistantRow}`}
      style={{ display: 'grid', gridTemplateColumns: isUser ? '1fr auto' : 'auto 1fr', alignItems: 'start', gap: '12px', width: '100%' }}
    >
      {/* Assistant avatar */}
      {!isUser && (
        <div className={styles.aiAvatarWrapper}>
          <Avatar
            className={`${styles.avatar} ${isAiParticipant ? styles.participantAvatar : ''}`}
          >
            {isAiParticipant ? (
              <>
                <AvatarImage
                  src={
                    (messageWithPersona.personaId &&
                      participantAvatarMap?.[messageWithPersona.personaId]) ||
                    (messageWithPersona.personaIcon &&
                    /^https?:\/\/|^\//.test(messageWithPersona.personaIcon)
                      ? messageWithPersona.personaIcon
                      : null) ||
                    getRobotAvatar(String(messageWithPersona.personaId))
                  }
                />
                <AvatarFallback className={styles.personaIconFallback}>
                  {messageWithPersona.personaIcon &&
                  !/^https?:\/\/|^\//.test(messageWithPersona.personaIcon) ? (
                    <span style={{ fontSize: 16 }}>{messageWithPersona.personaIcon}</span>
                  ) : (
                    <Bot size={14} />
                  )}
                </AvatarFallback>
              </>
            ) : (
              <>
                <AvatarImage src={AI_AVATAR_URL} />
                <AvatarFallback>
                  <Bot size={14} />
                </AvatarFallback>
              </>
            )}
          </Avatar>
          <span className={styles.aiStatusDot} title="Online" />
          {isAiParticipant && personaName && (
            <span className={styles.personaNameLabel}>{personaName}</span>
          )}
        </div>
      )}
      <div className={styles.messageContentWrapper} style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          className={`${styles.bubbleWrapper} ${isUser ? styles.userBubbleWrapper : styles.assistantBubbleWrapper}`}
        >
          <div
            className={`${styles.bubble} ${
              isUser
                ? isParticipant
                  ? getParticipantBubbleClass(message.userId)
                  : styles.userBubble
                : isAiParticipant
                  ? styles.aiParticipantBubble
                  : styles.assistantBubble
            }`}
          >
            {/* Display attachment if present — GLB gets 3D viewer, images get img tag */}
            {isUser &&
              (message as any).imageAttachment?.content &&
              typeof (message as any).imageAttachment.content === 'string' &&
              (isGlbFile((message as any).imageAttachment.name || '') ||
                isGlbFile((message as any).imageAttachment.content)) ? (
                <div
                  onClick={() => setGlbModal({
                    src: (message as any).imageAttachment.content,
                    name: (message as any).imageAttachment.name,
                  })}
                  style={{ cursor: 'pointer' }}
                  title="Click to expand 3D viewer"
                >
                  <GlbViewer
                    src={(message as any).imageAttachment.content}
                    name={(message as any).imageAttachment.name}
                    compact
                  />
                </div>
              ) : isUser &&
              (message as any).imageAttachment?.content &&
              typeof (message as any).imageAttachment.content === 'string' &&
              (message as any).imageAttachment.content.startsWith('data:image/') ? (
                <div className={styles.attachmentPreview}>
                  <img
                    src={(message as any).imageAttachment.content}
                    alt={(message as any).imageAttachment.name || 'Attached image'}
                    className={styles.attachmentImage}
                    onError={(e) => {
                      console.error('[AiChatMessage] Failed to load image attachment');
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span className={styles.attachmentName}>
                    {(message as any).imageAttachment.name}
                  </span>
                </div>
              ) : null}
            {/* Render message content — structured (with phase pills) or plain */}
            {renderContent()}
          </div>
          {/* Copy button moved to be directly underneath message box on right side */}
          {renderActions()}
          {!isUser && !isStreaming && sessionId && secretCandidates.length > 0 && (
            <div className={styles.secretCaptureCard}>
              <div className={styles.secretCaptureHeader}>Detected secret suggestions</div>
              <div className={styles.secretCaptureList}>
                {secretCandidates.map((candidate) => (
                  <div key={candidate.keyName} className={styles.secretCaptureItem}>
                    <div className={styles.secretCaptureMeta}>
                      <code>{candidate.keyName}</code>
                      <span>{maskSecretValue(candidate.value)}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openSecretDialog(candidate)}>
                      Add to secrets
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Auto-applied recommendations removed — user preference */}
          {/* Auto-speak AI participant messages via TTS — only for recent messages (not history) */}
          {isAiParticipant &&
            !isStreaming &&
            message.content &&
            message.createdAt &&
            Date.now() - new Date(message.createdAt).getTime() < 30_000 && (
              <AiParticipantSpeaker
                text={message.content}
                personaId={messageWithPersona.personaId || ''}
                personaName={personaName || undefined}
                autoSpeak={true}
              />
            )}
        </div>
        <span className={styles.timestamp}>
          {message.createdAt ? formatTimeOnly(message.createdAt) : ''}
          {!isUser && (message as any).model && (
            <span className={styles.modelTag}>{(message as any).model}</span>
          )}
        </span>
      </div>

      <Dialog open={isSecretDialogOpen} onOpenChange={setIsSecretDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Secret</DialogTitle>
            <DialogDescription>
              This value will be encrypted and stored in your session/project secrets.
            </DialogDescription>
          </DialogHeader>
          <div className={styles.secretDialogFields}>
            <div className={styles.secretDialogField}>
              <label>Key Name</label>
              <Input
                value={secretKeyName}
                onChange={(e) => setSecretKeyName(e.target.value.toUpperCase())}
                placeholder="API_KEY"
              />
            </div>
            <div className={styles.secretDialogField}>
              <label>Value</label>
              <div className={styles.secretValueRow}>
                <Input
                  type={showSecretValue ? 'text' : 'password'}
                  value={secretValue}
                  onChange={(e) => setSecretValue(e.target.value)}
                  placeholder="Secret value"
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowSecretValue((s) => !s)}
                >
                  {showSecretValue ? <EyeOff size={14} /> : <Eye size={14} />}
                </Button>
              </div>
            </div>
            <div className={styles.secretDialogField}>
              <label>Description (optional)</label>
              <Textarea
                value={secretDescription}
                onChange={(e) => setSecretDescription(e.target.value)}
                rows={2}
                placeholder="What this secret is used for"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsSecretDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSecret} disabled={createSecretMutation.isPending}>
              {createSecretMutation.isPending ? 'Saving...' : 'Save Secret'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isUser && (
        <div className={styles.avatarContainer} style={{ flexShrink: 0 }}>
          <UserAvatar
            user={{
              displayName:
                message.userDisplayName ||
                ('displayName' in message ? message.displayName : null) ||
                currentUserDisplayName ||
                'You',
              avatarUrl: message.userAvatarUrl || userAvatarUrlFallback || null,
            }}
            status={userStatus || 'offline'}
            size="sm"
          />
        </div>
      )}
      {/* GLB 3D model fullscreen modal */}
      {glbModal && createPortal(
        <div
          className={styles.glbModalOverlay}
          onClick={() => setGlbModal(null)}
        >
          <div
            className={styles.glbModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.glbModalHeader}>
              <span className={styles.glbModalTitle}>{glbModal.name || '3D Model'}</span>
              <button
                className={styles.glbModalClose}
                onClick={() => setGlbModal(null)}
              >
                &times;
              </button>
            </div>
            <GlbViewer
              src={glbModal.src}
              name={glbModal.name}
              compact={false}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
